import React from 'react';
import { Modal } from 'react-bootstrap';
import { ServicesContext, usePatientService } from '~/services';
import { ErrorModal } from '~/components/Errors/ErrorModal';
import { enumValue, getGapsByCategory, gapIsAddressed, gapIsPending } from '~/util';
import { MediaPreview } from '~/components/MediaPreview';
import { BSModal } from '~/components/Bootstrap';
import { useRoleActions } from '~/util/useRoleActions';
import { QualitySubmitRow } from '.';

interface QualitySubmitProps {
    patient: PatientType;
    attachments: MediaFileType[];
    handleLoading: (x: boolean) => void;
    disabled: boolean;
}
export function QualitySubmit({
    patient,
    attachments,
    handleLoading,
    disabled,
}: QualitySubmitProps) {
    const context = React.useContext(ServicesContext);
    const { loadPatient } = usePatientService();
    const roleCanSubmit = useRoleActions('allow_create_esubmission_quality_gap');
    const [show, setShow] = React.useState(false);
    const [showError, setShowError] = React.useState(false);
    const [error, setError] = React.useState('');

    function handleSubmit() {
        if (attachments.length === 0) {
            setError('This eSDS cannot be submitted until attachments have been added.');
            setShowError(true);
        } else {
            setShowError(false);
            setShow(true);
        }
    }

    async function submitQualityGap() {
        handleLoading(true);

        try {
            await context.patient.submitSubmission({
                patient: {
                    id: patient.id,
                },
                gap_type: enumValue(['gap', 'type', 'Quality Gap']),
            });
        } catch {
            // empty catch
        } finally {
            handleLoading(false);
            setShow(false);
            loadPatient(patient.id);
        }
    }

    const canSubmit = patient.quality_gaps.some(
        (gap) => gap.submission_answer && (gapIsPending(gap) || gapIsAddressed(gap))
    );

    const gapsByCategory = getGapsByCategory(
        patient.quality_gaps.filter((gap) => !!gap.submission_answer),
        enumValue(['gap', 'status', 'Pending']) as number
    );

    return (
        <>
            <button
                id="quality-submit"
                type="submit"
                className="btn btn-info"
                onClick={handleSubmit}
                disabled={!canSubmit || disabled}
            >
                Submit
            </button>
            <BSModal
                className="attestation-modal-card foc-gap-modal"
                size="lg"
                show={show}
                onHide={() => setShow(false)}
                aria-labelledby="qua-submit-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="qua-submit-title" role="heading" aria-level={1}>
                        Review SDS Submission
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!roleCanSubmit && (
                        <div className="permissions-alert">
                            You don’t have permission to create eSubmissions on this record.
                        </div>
                    )}
                    {gapsByCategory.map(
                        (cat, idx) =>
                            cat.gaps.length > 0 && (
                                <div className="foc-gap-category-container" key={cat.name}>
                                    <div>
                                        <div className={`h5 fw-bold mb-0 ${idx > 0 ? 'mt-2' : ''}`}>
                                            {cat.name}
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col">
                                            <table className="table table-sm gap-table quality-gap-table table-striped w-100">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th colSpan={2} className="w-50">
                                                            Measure
                                                        </th>
                                                        <th className="w-20 text-center">Date</th>
                                                        <th className="w-30 text-center">
                                                            Results
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {cat.gaps.map((g: GapType) => (
                                                        <QualitySubmitRow
                                                            key={g.id}
                                                            qualityGapMeasure={{
                                                                ...g.measure!.quality_measure!,
                                                                quality_measure_details:
                                                                    g.measure!
                                                                        .quality_measure_details,
                                                            }}
                                                            submissionAnswer={g.submission_answer}
                                                        />
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )
                    )}
                    <hr />
                    <div className="row mt-2">
                        <div className="col fw-bold">Attachments:</div>
                    </div>
                    <div className="row">
                        <div className="col-11 ms-2">
                            {attachments.length > 0
                                ? attachments.map((x) => (
                                      <div key={x.id}>
                                          <MediaPreview
                                              media={x}
                                              gapType={
                                                  enumValue([
                                                      'gap',
                                                      'type',
                                                      'Quality Gap',
                                                  ]) as number
                                              }
                                          />
                                      </div>
                                  ))
                                : 'None'}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => setShow(false)}
                    >
                        {roleCanSubmit ? 'Cancel' : 'Close'}
                    </button>

                    {roleCanSubmit && (
                        <button
                            id="quality-submit-confirm"
                            type="submit"
                            className="btn btn-primary"
                            onClick={submitQualityGap}
                            disabled={disabled}
                        >
                            {disabled && <i className="fa-solid fa-spin fa-spinner me-2" />}
                            Submit
                        </button>
                    )}
                </Modal.Footer>
            </BSModal>
            <ErrorModal
                show={showError}
                showSubmit={false}
                handleClose={() => setShowError(false)}
                handleSubmit={handleSubmit}
                title="Attachments Error"
                description={error}
            />
        </>
    );
}
